import React, { lazy } from 'react';

/**
 * 效能優化工具
 */

// 代碼分割 - 懶載入頁面組件
export const LazyComponents = {
  // Senior 版本組件
  SeniorKeyinPage: lazy(() => import('../pages/keyin/SeniorKeyinPage')),
  SeniorBoardPage: lazy(() => import('../pages/board/SeniorBoardPage')),
  SeniorProdPage: lazy(() => import('../pages/prod/SeniorProdPage')),
  SeniorHistoryPage: lazy(() => import('../pages/history/SeniorHistoryPage')),
  SeniorFixPage: lazy(() => import('../pages/fix/SeniorFixPage')),
  PriceChangePage: lazy(() => import('../pages/price/PriceChangePage')),

  // 注意：傳統版本組件已遷移至 _archive/legacy-antd-files/

  // 開發工具（僅開發環境）
  ErrorHandlingTest: process.env.NODE_ENV === 'development' 
    ? lazy(() => import('../components/ErrorHandlingTest'))
    : null
};

/**
 * 記憶體優化 Hook
 */
export function useMemoryOptimization() {
  // 清理未使用的事件監聽器
  const cleanupEventListeners = () => {
    // 移除所有自定義事件監聽器
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.removeEventListener(event, () => {}, true);
    });
  };

  // 頁面卸載時清理
  React.useEffect(() => {
    return () => {
      cleanupEventListeners();
    };
  }, []);
}

/**
 * 圖片預載入優化
 */
export function preloadCriticalImages(imageUrls) {
  imageUrls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = url;
    document.head.appendChild(link);
  });
}

/**
 * Firebase 連接池優化
 */
export class FirebaseConnectionPool {
  static connections = new Map();
  static maxConnections = 5;

  static getConnection(key) {
    if (this.connections.size >= this.maxConnections) {
      // 移除最舊的連接
      const firstKey = this.connections.keys().next().value;
      this.connections.delete(firstKey);
    }
    
    if (!this.connections.has(key)) {
      this.connections.set(key, Date.now());
    }
    
    return this.connections.get(key);
  }

  static cleanup() {
    this.connections.clear();
  }
}

/**
 * 本地儲存優化
 */
export class LocalStorageOptimizer {
  static MAX_SIZE = 5 * 1024 * 1024; // 5MB
  static CACHE_PREFIX = 'purchase_cache_';

  static setItem(key, value, ttl = 3600000) { // 預設 1 小時 TTL
    try {
      const data = {
        value,
        timestamp: Date.now(),
        ttl
      };
      
      const serialized = JSON.stringify(data);
      
      // 檢查大小限制
      if (serialized.length > this.MAX_SIZE) {
        console.warn('資料過大，無法儲存到 LocalStorage');
        return false;
      }
      
      localStorage.setItem(`${this.CACHE_PREFIX}${key}`, serialized);
      return true;
    } catch (error) {
      console.error('LocalStorage 寫入失敗:', error);
      return false;
    }
  }

  static getItem(key) {
    try {
      const cached = localStorage.getItem(`${this.CACHE_PREFIX}${key}`);
      if (!cached) {return null;}

      const data = JSON.parse(cached);
      const now = Date.now();

      // 檢查是否過期
      if (now - data.timestamp > data.ttl) {
        this.removeItem(key);
        return null;
      }

      return data.value;
    } catch (error) {
      console.error('LocalStorage 讀取失敗:', error);
      this.removeItem(key);
      return null;
    }
  }

  static removeItem(key) {
    localStorage.removeItem(`${this.CACHE_PREFIX}${key}`);
  }

  static clear() {
    Object.keys(localStorage)
      .filter(key => key.startsWith(this.CACHE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  }

  static getStorageSize() {
    let total = 0;
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        total += localStorage.getItem(key).length;
      }
    });
    return total;
  }
}

/**
 * 查詢優化器
 */
export class QueryOptimizer {
  static queryCache = new Map();
  static CACHE_DURATION = 30000; // 30秒快取

  static async optimizedQuery(queryKey, queryFunction, useCache = true) {
    if (useCache && this.queryCache.has(queryKey)) {
      const cached = this.queryCache.get(queryKey);
      if (Date.now() - cached.timestamp < this.CACHE_DURATION) {
        console.log(`使用快取查詢: ${queryKey}`);
        return cached.data;
      }
    }

    console.log(`執行新查詢: ${queryKey}`);
    const data = await queryFunction();
    
    if (useCache) {
      this.queryCache.set(queryKey, {
        data,
        timestamp: Date.now()
      });

      // 清理過期快取
      this.cleanupExpiredCache();
    }

    return data;
  }

  static cleanupExpiredCache() {
    const now = Date.now();
    Array.from(this.queryCache.entries()).forEach(([key, value]) => {
      if (now - value.timestamp > this.CACHE_DURATION) {
        this.queryCache.delete(key);
      }
    });
  }

  static clearCache() {
    this.queryCache.clear();
  }
}

/**
 * 載入狀態管理器
 */
export class LoadingManager {
  static loadingStates = new Map();

  static setLoading(key, isLoading) {
    this.loadingStates.set(key, isLoading);
    this.notifySubscribers();
  }

  static isLoading(key) {
    return this.loadingStates.get(key) || false;
  }

  static hasAnyLoading() {
    return Array.from(this.loadingStates.values()).some(loading => loading);
  }

  static subscribers = new Set();

  static subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  static notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.loadingStates));
  }

  static clear() {
    this.loadingStates.clear();
    this.notifySubscribers();
  }
}

// React Hook 用於載入狀態
export function useGlobalLoading() {
  const [loadingStates, setLoadingStates] = React.useState(new Map());

  React.useEffect(() => {
    const unsubscribe = LoadingManager.subscribe(setLoadingStates);
    return unsubscribe;
  }, []);

  const hasAnyLoading = React.useCallback(() => {
    if (!loadingStates || loadingStates.size === 0) {return false;}
    return Array.from(loadingStates.values()).some(loading => loading);
  }, [loadingStates]);

  return {
    setLoading: LoadingManager.setLoading,
    isLoading: LoadingManager.isLoading,
    hasAnyLoading,
    loadingStates
  };
}

const PerformanceOptimization = {
  LazyComponents,
  useMemoryOptimization,
  preloadCriticalImages,
  FirebaseConnectionPool,
  LocalStorageOptimizer,
  QueryOptimizer,
  LoadingManager,
  useGlobalLoading
};

export default PerformanceOptimization;
